import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';
import { AutoRedemptionSettingFacade } from '@features/auto-redemption-settings/facades/auto-redemption-setting/auto-redemption-setting.facade';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { Store } from '@ngrx/store';
import { UnleashFacade } from '@rd/core/facades/unleash-facade/unleash-facade.ts';
import { getElementBy } from '@rd/utils/getElementBy';
import { filter, map, of, switchMap } from 'rxjs';
import { EverydaySpendState } from '../store/reducers/everyday-spend.reducer';
import { getCheckoutFormDataById } from '../store/selectors/everyday-spend.selectors';
import { EVERYDAY_SPEND_UNLEASH_KEY } from '../tokens/unleash-flags.token';

export const everydaySpendCanMatch: CanMatchFn = () => {
  const unleashFacade = inject(UnleashFacade);
  const unleashService = inject(UnleashService);

  // Should wait for Unleash client to be ready
  // before checking else the flag won't have the correct value
  // Because canMatch is called before Unleash client is finished fetching
  // causing the flag to have the wrong value (only for user navigate directly to edr route)
  return unleashFacade.isUnleashClientReady$.pipe(
    filter(Boolean),
    map(() => unleashService.isEnabled(EVERYDAY_SPEND_UNLEASH_KEY)),
  );
};

export const formDataGuard: CanActivateFn = (route) => {
  const store = inject<
    Store<{
      everydaySpend: EverydaySpendState;
    }>
  >(Store);
  const router = inject(Router);

  const loyaltyProgramId = route.queryParamMap.get('loyaltyProgramId');

  if (!loyaltyProgramId) {
    return of(router.createUrlTree(['/everyday_rewards']));
  }

  return store.select(getCheckoutFormDataById(loyaltyProgramId)).pipe(
    map((formData) => {
      if (formData) {
        return true;
      } else {
        return router.createUrlTree(['/everyday_rewards']);
      }
    }),
  );
};

export const autoRedemptionEnableGuard: CanActivateFn = (route) => {
  const router = inject(Router);

  const loyaltyProgramId = route.paramMap.get('id');

  if (!loyaltyProgramId) {
    return of(router.createUrlTree(['/everyday_rewards']));
  }

  const loyaltyProgramFacade = inject(LoyaltyProgramFacade);
  const autoRedemptionSettingFacade = inject(AutoRedemptionSettingFacade);

  return loyaltyProgramFacade.retailLoyaltyPrograms$.pipe(
    getElementBy(loyaltyProgramId),
    switchMap((loyaltyProgram) => {
      if (!loyaltyProgram) {
        return of(router.createUrlTree(['/everyday_rewards']));
      }

      return autoRedemptionSettingFacade.autoRedemptionRulesMap$.pipe(
        map(
          (arRulesMap) => arRulesMap[loyaltyProgram.transferConnectIdentifier],
        ),
        map((arRule) => {
          if (!arRule || arRule.enabled) {
            return true;
          }

          return router.createUrlTree(['/everyday_rewards']);
        }),
      );
    }),
  );
};
